<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <q-icon name="far fa-list-alt" class="q-mb-sm" /> {{ $t('tasks') }}
        </div>

        <q-list class="q-mb-md">
            <q-collapsible icon="fas fa-filter" :label="$t('filter')" v-model="show_filter">
                <div class="row gutter-sm">
                    <div class="col-xs-12 col-sm-6">
                        <q-input v-model="representative" :float-label="$t('representative')" clearable @keydown.enter="request({ pagination: { page: 1, rowsNumber: 10, rowsPerPage: 5, descending: false, sortBy: null } })" />
                    </div>

                    <div class="col-xs-12 col-sm-6">
                        <q-select v-model="status" :options="statuses" :float-label="$t('status')" clearable @keydown.enter="request({ pagination: { page: 1, rowsNumber: 10, rowsPerPage: 5, descending: false, sortBy: null } })" />
                    </div>

                    <div class="col-xs-12 col-sm-6">
                        <q-datetime v-model="start_date" :float-label="$t('start_date')" type="date" format="DD/MM/YYYY" clearable @keydown.enter="request({ pagination: { page: 1, rowsNumber: 10, rowsPerPage: 5, descending: false, sortBy: null } })" />
                    </div>

                    <div class="col-xs-12 col-sm-6">
                        <q-datetime v-model="end_date" :float-label="$t('end_date')" type="date" format="DD/MM/YYYY" clearable @keydown.enter="request({ pagination: { page: 1, rowsNumber: 10, rowsPerPage: 5, descending: false, sortBy: null } })" />
                    </div>
                </div>

                <div class="row gutter-sm justify-end q-mt-sm">
                    <div class="col-xs-12 col-sm-3">
                        <q-btn color="secondary" class="full-width" :label="$t('apply_filter')" icon="fas fa-filter" @click="request({ pagination: { page: 1, rowsNumber: 10, rowsPerPage: 5, descending: false, sortBy: null } })" />
                    </div>
                </div>
            </q-collapsible>
        </q-list>

        <q-table
            :data="tasks"
            :columns="columns"
            :pagination.sync="server_pagination"
            @request="request"
            :loading="loading"
            selection="multiple"
            :selected.sync="selected"
            row-key="id"
            :no-data-label="$t('no_data_avaliable')"
            :no-results-label="$t('no_results_found')"
            :loading-label="$t('loading_data')"
            :rows-per-page-label="$t('items_per_page')"
            :rows-per-page-options="[5,10,50,100]"
            binary-state-sort
            >

            <template slot="top" slot-scope="props">
                <div class="col" />

                <q-btn color="negative" icon="fas fa-trash" :label="$t('remove')" @click="confirm_remove = true" v-if="selected.length > 0" class="q-mr-md" />

                <q-btn-dropdown color="secondary" icon="fas fa-chart-line" class="q-mr-sm" v-model="dropdown_reports">
                    <q-list link>
                        <q-item v-for="(report, index) in reports" :key="index" @click.native="showReport(report)">
                            <q-item-main :label="$t(report.slug)" />
                        </q-item>
                    </q-list>
                </q-btn-dropdown>

                <q-btn color="secondary" icon="fas fa-plus" :label="$t('new')" @click="$router.push({ name: '4traders_task', params: { id: 0 } })" />
            </template>

            <q-tr slot="header" slot-scope="props" :props="props">
                <q-th auto-width>
                    <q-checkbox v-model="props.selected" indeterminate-value="some" />
                </q-th>

                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                    {{ $t(col.label) }}
                </q-th>
            </q-tr>

            <q-tr slot="body" slot-scope="props" :props="props">
                <q-td auto-width>
                    <q-checkbox color="primary" v-model="props.selected" />
                </q-td>

                <q-td auto-width key="id" :props="props">
                    {{ props.row.id }}
                </q-td>

                <q-td key="operator_name" :props="props">
                    {{ props.row.operator_name }}
                </q-td>

                <q-td key="task_date" :props="props">
                    {{ format(props.row.task_date) }}
                </q-td>

                <q-td key="qty_tasks" :props="props">
                    {{ props.row.qty_tasks }}
                </q-td>

                <q-td key="author_name" :props="props">
                    {{ props.row.author_name }}
                </q-td>

                <q-td key="status" :props="props" auto-width>
                    <q-chip small :icon="getIconStatus(props.row.status)" :color="getColorStatus(props.row.status)" class="full-width">
                        {{ $t(getStatus(props.row.status)) }}
                    </q-chip>
                </q-td>

                <q-td key="actions" :props="props">
                    <q-btn flat round color="secondary" icon="far fa-list-alt" @click="showAnswers(props.row)" :disabled="props.row.status === 1 ? true : false" />
                    <q-btn flat round color="primary" icon="fas fa-pencil-alt" @click="$router.push({ name: '4traders_task', params: { id: props.row.actions } })" />
                    <q-btn flat round color="negative" icon="far fa-trash-alt" @click="confirmRemove(props.row.actions)" />
                </q-td>
            </q-tr>
        </q-table>

        <q-dialog v-model="confirm_remove" @ok="removeItem">
            <span slot="title">{{ $t('remove_item') }}</span>

            <span slot="message">{{ $t('confirm_remove_item') }}</span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
                <q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
            </template>
        </q-dialog>

        <q-modal v-model="show_answers" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh'}" @hide="request({ pagination: server_pagination })">
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense @click="show_answers = false" icon="fas fa-times" />
                    <q-toolbar-title>
                        {{ $t('answers') }}
                    </q-toolbar-title>
                </q-toolbar>

                <div class="layout-padding">
                    <div class="row gutter-sm">
                        <div class="col-xs-12 col-sm-6" v-for="(step, index) in steps" :key="index" v-if="step.answered_collection && step.status != 4">
                            <q-card>
                                <q-card-title>
                                    {{ step.start_time ? step.start_time.slice(0, -3) : '' }}
                                    {{ step.start_time && step.end_time ? ' - ' : '' }}
                                    {{ step.end_time ? step.end_time.slice(0, -3) : '' }}
                                    {{ step.start_time || step.end_time ? ' / ' : '' }}
                                    {{ step.branch_name }}
                                </q-card-title>

                                <q-card-separator />

                                <q-card-actions class="justify-center">
                                    <q-btn flat :label="`${$t('check_in')} - ${step.checkin_date ? formatDateTime(step.checkin_date) : $t('not_realized')}`" @click="openMaps(step.checkin_lat, step.checkin_long, step.branch_lat, step.branch_long)" class="q-mr-sm" />
                                </q-card-actions>

                                <q-card-separator />

                                <q-card-main>
                                    <q-list>
                                        <q-collapsible :label="$t('answers')">
                                            <q-list no-border highlight>
                                                <q-item v-for="(answer, index) in step.answered_collection" :key="`a${index}`" multiline>
                                                    <q-item-main>
                                                        <q-item-tile label>
                                                            {{ answer.title }}
                                                        </q-item-tile>

                                                        <q-item-tile sublabel>
                                                            <span v-if="answer.question_type === 'single_choice' || answer.question_type === 'dissertative'" class="text-italic">
                                                                {{ answer.answer[0] }}
                                                            </span>

                                                            <span v-if="answer.question_type === 'upload'">
                                                                <img :src="answer.answer[0]" style="width: 100%; height: auto;" />
                                                            </span>

                                                            <q-list separator no-border v-if="answer.question_type === 'multiple_choices'" class="text-italic">
                                                                <q-item v-for="(answer_selected, index) in answer.answer" :key="`as${index}`">
                                                                    {{ answer_selected }}
                                                                </q-item>
                                                            </q-list>
                                                        </q-item-tile>
                                                    </q-item-main>
                                                </q-item>
                                            </q-list>
                                        </q-collapsible>
                                    </q-list>
                                </q-card-main>

                                <q-card-separator />

                                <q-card-actions class="justify-center">
                                    <q-btn flat :label="`${$t('check_out')} - ${step.checkout_date ? formatDateTime(step.checkout_date) : $t('not_realized')}`" @click="openMaps(step.checkout_lat, step.checkout_long, step.branch_lat, step.branch_long)" />
                                </q-card-actions>
                            </q-card>
                        </div>

                        <div class="col-xs-12 col-sm-6" v-for="(step, index) in steps" :key="index" v-if="step.status === 4">
                            <q-card>
                                <q-card-title>
                                    {{ step.start_time ? step.start_time.slice(0, -3) : '' }}
                                    {{ step.start_time && step.end_time ? ' - ' : '' }}
                                    {{ step.end_time ? step.end_time.slice(0, -3) : '' }}
                                    {{ step.start_time || step.end_time ? ' / ' : '' }}
                                    {{ step.branch_name }}
                                </q-card-title>

                                <q-card-separator />

                                <q-card-actions class="justify-center">
                                    <q-btn flat :label="`${$t('canceled')}`" class="q-mr-sm" />
                                </q-card-actions>

                                <q-card-separator />

                                <q-card-main>
                                    <q-list>
                                        <q-collapsible :label="$t('cancel_motive')">
                                            <q-list no-border highlight>
                                                <q-item>
                                                    <q-item-main>
                                                        <q-item-tile label>
                                                            {{ step.observation_cancel }}
                                                        </q-item-tile>
                                                    </q-item-main>
                                                </q-item>
                                            </q-list>
                                        </q-collapsible>
                                    </q-list>
                                </q-card-main>

                                <q-card-separator />

                                <q-card-actions class="justify-center">
                                    <q-btn flat :label="`${$t('canceled')}`" class="q-mr-sm" />
                                </q-card-actions>
                            </q-card>
                        </div>
                    </div>
                </div>
            </q-modal-layout>
        </q-modal>

        <q-modal v-model="show_report" :content-css="{minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh'}">
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense @click="show_report = false" icon="fas fa-times" />
                    <q-toolbar-title>
                        {{ $t('tasks') }}
                    </q-toolbar-title>
                </q-toolbar>

                <div class="layout-padding">
                    <q-table
                        :data="tasks_steps"
                        :columns="columns_tasks_steps"
                        :pagination.sync="server_pagination_tasks_steps"
                        @request="request_tasks_steps"
                        :loading="loading_tasks_steps"
                        row-key="name"
                        :no-data-label="$t('no_data_avaliable')"
                        :no-results-label="$t('no_results_found')"
                        :loading-label="$t('loading_data')"
                        :rows-per-page-label="$t('items_per_page')"
                        :rows-per-page-options="[5,10,50,100]"
                        binary-state-sort
                        >

                        <q-tr slot="header" slot-scope="props" :props="props">
                            <q-th v-for="col in props.cols" :key="col.name" :props="props">
                            {{ $t(col.label) }}
                            </q-th>
                        </q-tr>

                        <q-tr slot="body" slot-scope="props" :props="props">
                            <q-td key="company_name" :props="props">
                                {{ props.row.company_name }}
                            </q-td>

                            <q-td key="branch_name" :props="props">
                                {{ props.row.branch_name }}
                            </q-td>

                            <q-td key="operator_full_name" :props="props">
                                {{ props.row.operator_full_name }}
                            </q-td>

                            <q-td key="task_date" :props="props">
                                {{ formatDateOnly(props.row.task_date) }}
                            </q-td>

                            <q-td key="task_status" :props="props" auto-width>
                                 <q-chip small :icon="getIconStatus(props.row.task_status)" :color="getColorStatus(props.row.task_status)" class="full-width">
                                    {{ $t(getStatus(props.row.task_status)) }}
                                </q-chip>
                            </q-td>

                            <q-td key="step_time" :props="props">
                                {{ props.row.step_time }}
                            </q-td>

                            <q-td key="checkin_date" :props="props">
                                {{ formatDateTime(props.row.checkin_date) }}
                            </q-td>

                            <q-td key="checkout_date" :props="props">
                                {{ formatDateTime(props.row.checkout_date) }}
                            </q-td>

                            <q-td key="total_time" :props="props">
                                {{ props.row.total_time && props.row.total_time.days > 0 ? `${props.row.total_time.days} ${$t('days')}` : '' }} {{ props.row.total_time ? props.row.total_time.time : '' }}
                            </q-td>

                            <q-td key="status" :props="props" auto-width>
                                <q-chip small :icon="getIconStatus(props.row.status)" :color="getColorStatus(props.row.status)" class="full-width">
                                    {{ $t(getStatus(props.row.status)) }}
                                </q-chip>
                            </q-td>

                            <q-td key="collection_title" :props="props">
                                {{ props.row.collection_title }}
                            </q-td>

                            <q-td key="qty_collection_questions" :props="props">
                                {{ props.row.qty_collection_questions }}
                            </q-td>
                        </q-tr>
                    </q-table>
                </div>
            </q-modal-layout>
        </q-modal>

        <q-dialog v-model="confirm_remove" @ok="removeTasks">
            <span slot="title">{{ $t('remove_tasks') }}</span>

            <span slot="message">{{ $t('confirm_remove') }} {{ selected.length }} {{ $t(selected.length > 1 ? 'events' : 'event') }}?</span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
                <q-btn color="negative" :label="$t('remove')" @click="removeTasks()" />
            </template>
        </q-dialog>
    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
const { formatDate } = date
import { LocalStorage } from 'quasar'
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
    i18nOptions: { namespaces: ["common", "fortraders"] },

    data() {
        return {
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: null,
                descending: false,
                sortBy: null
            },
            server_pagination_tasks_steps: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: 50,
                descending: false,
                sortBy: null
            },
            loading: false,
            loading_tasks_steps: false,
            tasks: [],
            columns: [
                {
                    name: 'id',
                    required: true,
                    label: '#',
                    align: 'left',
                    field: 'name',
                    sortable: false,
                },
                {
                    name: 'operator_name',
                    required: true,
                    label: 'representative',
                    align: 'left',
                    field: 'operator_name',
                    sortable: false,
                },
                {
                    name: 'task_date',
                    required: true,
                    label: 'task_date',
                    align: 'left',
                    field: 'task_date',
                    sortable: false,
                },
                {
                    name: 'qty_tasks',
                    required: true,
                    label: 'quantity_tasks',
                    align: 'center',
                    field: 'qty_tasks',
                    sortable: false,
                },
                {
                    name: 'author_name',
                    required: true,
                    label: 'author',
                    align: 'left',
                    field: 'author_name',
                    sortable: false,
                },
                {
                    name: 'status',
                    required: true,
                    label: 'status',
                    align: 'left',
                    field: 'status',
                    sortable: false,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'right',
                    field: 'edit',
                    sortable: false,
                },
            ],
            columns_tasks_steps: [
                {
                    name: 'company_name',
                    required: true,
                    label: 'headquarter',
                    align: 'left',
                    field: 'company_name',
                    sortable: false,
                },
                {
                    name: 'branch_name',
                    required: true,
                    label: 'branch',
                    align: 'left',
                    field: 'branch_name',
                    sortable: false,
                },
                {
                    name: 'operator_full_name',
                    required: true,
                    label: 'representative',
                    align: 'left',
                    field: 'operator_full_name',
                    sortable: false,
                },
                {
                    name: 'task_date',
                    required: true,
                    label: 'event_date',
                    align: 'left',
                    field: 'task_date',
                    sortable: false,
                },
                {
                    name: 'task_status',
                    required: true,
                    label: 'event_status',
                    align: 'left',
                    field: 'task_status',
                    sortable: false,
                },
                {
                    name: 'step_time',
                    required: true,
                    label: 'task_time',
                    align: 'left',
                    field: 'step_time',
                    sortable: false,
                },
                {
                    name: 'checkin_date',
                    required: true,
                    label: 'check_in',
                    align: 'left',
                    field: 'checkin_date',
                    sortable: false,
                },
                {
                    name: 'checkout_date',
                    required: true,
                    label: 'check_out',
                    align: 'left',
                    field: 'checkout_date',
                    sortable: false,
                },
                {
                    name: 'total_time',
                    required: true,
                    label: 'total_time',
                    align: 'left',
                    field: 'total_time',
                    sortable: false,
                },
                {
                    name: 'status',
                    required: true,
                    label: 'task_status',
                    align: 'left',
                    field: 'status',
                    sortable: false,
                },
                {
                    name: 'collection_title',
                    required: true,
                    label: 'task',
                    align: 'left',
                    field: 'collection_title',
                    sortable: false,
                },
                {
                    name: 'qty_collection_questions',
                    required: true,
                    label: 'questions',
                    align: 'left',
                    field: 'qty_collection_questions',
                    sortable: false,
                },
            ],
            filter: '',
            action_id: '',
            confirm_remove: false,
            representative: '',
            task_date: null,
            show_answers: false,
            steps: [],
            start_date: null,
            end_date: null,
            dropdown_reports: false,
            reports: [{
                slug: 'tasks',
                id: 'tasks'
            }],
            tasks_steps: [],
            status: null,
            statuses: [
                { value: 1, label: this.$t('pending') },
                { value: 2, label: this.$t('realized') },
                { value: 3, label: this.$t('deleted') },
                { value: 4, label: this.$t('canceled') },
                { value: 5, label: this.$t('in_progress') },
                { value: 6, label: this.$t('questions_sent') },
            ],
            show_report: false,
            show_filter: false,
            selected: [],
            confirm_remove: false
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            num_rows: 'datatable/num_rows'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '90vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '90vw'
        }
    },

    methods: {
        request({ pagination }) {
            this.show_filter = false
            this.loading = true
            this.server_pagination = pagination

            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''

            let data = {
                "page_size": this.server_pagination.rowsPerPage,
                "page": this.server_pagination.page,
                "ordering": order_by,
                "search": this.representative,
                "start_date": date.formatDate(this.start_date, 'YYYY-MM-DD'),
                "end_date": date.formatDate(this.end_date, 'YYYY-MM-DD'),
                "status": this.status
            }

            this.saveFilter()
            this.savePagination()

            this.$axios.get(`/admin_tasks/tasks/?${this.$qs.stringify(data)}`, this.getHeader)
            .then(response => {
                this.server_pagination.rowsNumber = response.data.count

                this.tasks = []
                response.data.results.forEach(result => {
                    this.tasks.push({
                        id: result.id,
                        operator_name: result.operator_name,
                        author_name: result.author_name,
                        qty_tasks: result.qty_tasks,
                        task_date: result.task_date ? moment(result.task_date).tz('Etc/GMT').format('YYYY-MM-DDTHH:mm:ss.SSS') : null,
                        status: result.status,
                        task_steps: result.task_steps,
                        actions: result.id,
                        selected: false
                    })
                })

                this.loading = false
            })
            .catch(e => {
                this.loading = false
                console.log(e)
            })
        },

        request_tasks_steps({ pagination }) {
            this.loading_tasks_steps = true
            this.server_pagination_tasks_steps = pagination

            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''

            let data = {
                "page_size": this.server_pagination_tasks_steps.rowsPerPage,
                "page": this.server_pagination_tasks_steps.page,
                "ordering": order_by,
                "search": this.representative,
                "start_date": date.formatDate(this.start_date, 'YYYY-MM-DD'),
                "end_date": date.formatDate(this.end_date, 'YYYY-MM-DD'),
                "status": this.status
            }

            this.$axios.get(`/admin_tasks/tasks/list_task_steps/?${this.$qs.stringify(data)}`, this.getHeader)
            .then(response => {
                this.server_pagination_tasks_steps.rowsNumber = response.data.count
                this.tasks_steps = response.data.results

                this.loading_tasks_steps = false
            })
            .catch(e => {
                this.loading_tasks_steps = false
                console.log(e)
            })
        },

        showReport(report) {
            this.dropdown_reports = false
            this.show_report = true

            if (report.id === 'tasks') {
                this.request_tasks_steps({
                    pagination: this.server_pagination_tasks_steps
                })
            }
        },

        showAnswers(task) {
            this.steps = task.task_steps
            this.show_answers = true
        },

        confirmRemove(id) {
            this.action_id = id
            this.confirm_remove = true
        },

        removeItem() {
            this.$q.loading.show()

            this.$axios.delete(`/admin_tasks/tasks/${this.action_id}/`, this.getHeader)
            .then(response => {
                this.$q.loading.hide()

                this.request({
                    pagination: this.server_pagination
                })

                this.confirm_remove = false

                this.$q.notify({
                    message: this.$t('item_removed_success'),
                    type: 'positive',
                    icon: 'far fa-trash-alt'
                })
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        getStatus(status) {
            if (status == 1) return 'pending'
            else if (status == 2) return 'realized'
            else if (status == 3) return 'deleted'
            else if (status == 4) return 'canceled'
            else if (status == 5) return 'in_progress'
            else if (status == 6) return 'questions_sent'
        },

        getIconStatus(status) {
            if (status == 1) return 'fas fa-clock'
            else if (status == 2) return 'fas fa-check-double'
            else if (status == 3) return 'fas fa-trash-alt'
            else if (status == 4) return 'fas fa-ban'
            else if (status == 5) return 'fas fa-spinner'
            else if (status == 6) return 'fas fa-check'
        },

        getColorStatus(status) {
            if (status == 1) return 'warning'
            else if (status == 2) return 'positive'
            else if (status == 3) return 'negative'
            else if (status == 4) return 'faded'
            else if (status == 5) return 'info'
            else if (status == 6) return 'info'
        },

        format(date_raw) {
            return date.formatDate(date_raw, 'DD/MM/YYYY')
        },

        formatDateTime(date_raw) {
            return date.formatDate(date_raw, 'DD/MM/YYYY HH:mm')
        },

        formatDateOnly(date_raw) {
            return date.formatDate(date_raw, 'DD/MM/YYYY')
        },

        openMaps(lat, long, branch_lat, branch_long) {
            if (this.$q.platform.is.cordova && this.$q.platform.is.ios) {
                cordova.InAppBrowser.open(`https://www.google.com.br/maps/dir/${lat},${long}/${branch_lat},${branch_long}`, '_system', 'usewkwebview=yes,hidenavigationbuttons=yes,toolbar=no')
            }
            else {
                window.open(`https://www.google.com.br/maps/dir/${lat},${long}/${branch_lat},${branch_long}`, '_blank')
            }
        },

        savePagination() {
            this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
        },

        saveFilter() {
            let dataTableTask = {
              representative: this.representative,
              start_date: this.start_date,
              end_date: this.end_date,
              status:  this.status
            }
            this.$q.localStorage.set('dataTableTask', dataTableTask)
        },

        loadFilter() {
          if (this.$q.localStorage.has('dataTableTask')) {
            let task = this.$q.localStorage.get.item('dataTableTask')
            this.representative = task.representative
            this.start_date = task.start_date
            this.end_date = task.end_date
            this.status = task.status
          }
        },

        loadPagination() {
            this.server_pagination.rowsPerPage = this.num_rows
        },

        removeTasks() {
            this.$q.loading.show()

            let task_list = this.selected.map((e) => e.id)

            let data = {
                'task_list': task_list
            }

            this.$axios.post(`/admin_tasks/tasks/destroy_list/`, data, this.getHeader)
            .then(response => {
                this.request({
                    pagination: {
                        page: 1,
                        rowsNumber: 10,
                        rowsPerPage: 5,
                        descending: false,
                        sortBy: null
                    }
                })

                this.confirm_remove = false

                this.$q.notify({
                    message: this.$t('item_removed_success'),
                    type: 'positive',
                    icon: 'far fa-trash-alt'
                })
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
            })
        }
    },

    created() {
        this.loadPagination()
        this.loadFilter()

        this.request({
            pagination: this.server_pagination
        })
    }
}
</script>

<style lang="stylus" scoped>

</style>
